import globalText from "../../../utils/globalText";
import { globalTextFormat } from "../../../utils/globalTextFormat";
import { linkIdentifier } from "../../../utils/linkIdentifier";
import FileFormatMapper from "../FileFormatMapper";

/**
 * @typedef {Object} AdditionalDetailsPreviewMapperProps
 * @property {Array<Object>} mappingData - List of additional fields with name, value, and data_type
 * @property {Object<string, string>} previewImages - Preloaded image URLs for uploaded files (keyed by field name & type)
 */

/**
 * Renders a read-only preview of all dynamic additional details.
 * Automatically handles all field types including upload, hyperlink,
 * date range, toggle, multi-select, month-year, and year ranges.
 *
 * @param {AdditionalDetailsPreviewMapperProps} props
 */

const AdditionalDetailsPreviewMapper = ({ mappingData, previewImages }) => {
  const safeMapping = Array.isArray(mappingData) ? mappingData : [];
  const safePreviewImages = previewImages || {};
  const LASTINDEX = safeMapping.length - 1;

  const convertToMonthYear = (input) => {
    if (!input) return globalText.NOT_GIVEN;

    const [month, year] = input.split(/[-,/ ]+/);
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const monthIndex = parseInt(month, 10);
    const monthName = monthNames[monthIndex - 1];

    return monthName && year ? `${monthName}, ${year}` : globalText.NOT_GIVEN;
  };

  return (
    <div className="bg-primary-card rounded-border-radius-md p-padding-md w-width-full flex flex-col gap-gap-md Qsmd:mb-margin-md mb-margin-base">
      <span className="text-font-md font-semibold text-text-primary">
        {globalTextFormat(globalText.ADDITIONAL_DETAILS, [0, 1])}
      </span>

      <div className="w-width-full flex flex-col">
        {safeMapping.length === 0 ? (
          <p className="text-text-secondary text-font-md">
            {globalText.ADDITIONAL_DETAILS_UNSUPPORTED}
          </p>
        ) : (
          safeMapping.map((field, index) => {
            const hasValue =
              field?.value &&
              field?.value !== globalText.NOT_GIVEN &&
              (field?.data_type !== "multi_select_list"
                ? true
                : field?.value?.length > 0);

            const value =
              field?.data_type === "upload"
                ? hasValue
                  ? field?.value?.name
                  : globalText.NOT_GIVEN
                : field?.data_type === "time"
                  ? field?.value
                  : field?.data_type === "hyperlink"
                    ? field?.value
                    : field?.data_type === "from_year_to_year"
                      ? field?.value?.from && field?.value?.to
                        ? `${field?.value?.from} - ${field?.value?.to}`
                        : globalText.NOT_GIVEN
                      : field?.data_type === "month_year"
                        ? convertToMonthYear(field?.value)
                        : field?.data_type === "multi_select_list"
                          ? field?.value?.length
                            ? field?.value.join(", ")
                            : globalText.NOT_GIVEN
                          : field?.data_type === "date_range"
                            ? field?.value?.start_date && field?.value?.end_date
                              ? `${field?.value?.start_date} - ${field?.value?.end_date}`
                              : globalText.NOT_GIVEN
                            : field?.data_type === "toggle"
                              ? field?.value === true
                                ? "Yes"
                                : globalText.NOT_GIVEN
                              : field?.value || globalText.NOT_GIVEN;

            return (
              <div key={`${field?.name}_${field?.data_type}_${index}`}>
                <div className="w-width-full flex flex-col gap-gap-xs">
                  <p
                    className={`text-font-md text-text-primary overflow-hidden whitespace-pre-line ${
                      field?.data_type === "hyperlink"
                        ? "truncate"
                        : "break-all"
                    }`}
                  >
                    {field?.data_type === "upload" ? (
                      hasValue ? (
                        <div className="flex justify-between items-start gap-gap-sm w-width-full">
                          <div className="flex-1 min-w-min-width-base">
                            <p className="break-all">
                              {linkIdentifier(field?.value?.name)}
                            </p>
                            <p className="text-font-xs text-text-secondary">
                              {field?.name}
                            </p>
                          </div>

                          <div className="flex items-center justify-end">
                            <img
                              src={
                                (["PNG", "BMP", "JPEG", "JPG"].includes(
                                  field?.value?.type
                                    ?.split("/")
                                    ?.pop()
                                    ?.toUpperCase(),
                                ) &&
                                  safePreviewImages[
                                    `${field?.name}_${field?.data_type}`
                                  ]) ||
                                FileFormatMapper(
                                  field?.value?.name
                                    ?.split(".")
                                    .pop()
                                    ?.toLowerCase(),
                                  "confirm",
                                )
                              }
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src =
                                  "https://q2pay.in/new_assets/common/file_format/default.svg";
                              }}
                              alt="file"
                              className="w-icon-lg h-icon-lg shrink-0"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-gap-xs w-width-full">
                          <p className="break-all">{globalText.NOT_GIVEN}</p>
                          <p className="text-font-xs text-text-secondary">
                            {field?.name}
                          </p>
                        </div>
                      )
                    ) : field?.data_type === "hyperlink" ? (
                      field?.value && field?.value !== globalText.NOT_GIVEN ? (
                        linkIdentifier(field?.value)
                      ) : (
                        globalText.NOT_GIVEN
                      )
                    ) : (
                      linkIdentifier(value || globalText.NOT_GIVEN)
                    )}
                  </p>

                  {field?.data_type !== "upload" && (
                    <p className="text-font-xs text-text-secondary">
                      {typeof field?.value === "object" &&
                      field?.data_type === "from_year_to_year"
                        ? "From year - To year"
                        : field?.name}
                    </p>
                  )}
                </div>

                {index !== LASTINDEX && (
                  <div className="border-b border-primary-bg my-margin-sm" />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdditionalDetailsPreviewMapper;
